/**
 * The subject page and *Subjekty* between the screen and the API (UC-4, slice 01). The API names
 * a subject by its display name only, so the whole name goes in `givenName` (as the text check
 * does) and the short id in `subjectNo`; with `enrolledAt` an unnamed subject reads
 * "Neznámy · 14:05 · 7f3a". Values the workspace does not know (an encounter type, a legal
 * status, a capture kind from elsewhere) are left out rather than guessed.
 */
import type { EncounterPerson } from '@/components/humint/encounter/encounter-types'
import type {
  RecordingEncounter,
  RecordingListItem,
} from '@/components/humint/questioning/recording-types'
import type { SubjectPageProblem } from '@/components/humint/subject/SubjectPageNotice'
import type { SubjectPageData } from '@/components/humint/subject/subject-types'
import {
  CAPTURE_KINDS,
  type CaptureKind,
  type CaptureSummary,
  type ChecklistStep,
  type CheckVerdict,
  type EncounterSummary,
  type EncounterType,
  type GeoPosition,
  LEGAL_STATUSES,
  type LegalStatus,
  type ListReadProblem,
  SKIP_REASONS,
  type SkipReason,
  type SubjectSummary,
} from '@/components/humint/types'
import {
  VOICE_SAMPLE_FLAGS,
  VOICE_SAMPLE_GRADES,
  type VoiceSampleGrade,
  type VoiceSampleQuality,
} from '@/components/humint/voice/voice-sample-quality'
import { readStage } from '@/components/processing/processing-stages'
import { ApiError } from '@/services/api/client'
import type {
  EncounterPlaceDto,
  RecordingEncounterDto,
  RecordingListItemDto,
  SubjectCaptureDto,
  SubjectChecklistDto,
  SubjectEncounterDto,
  SubjectListItemDto,
  SubjectPageResponse,
  SubjectSummaryDto,
  VoiceQualityDto,
} from '@/services/api/humint-subject-page'
import { toMgrs } from '../geo/mgrs'
import { importedFields } from '../probe/photo-origin'
import { withSubjectVoiceLeads, withVoiceLeads } from '../voice/subject-voice-mappers'
import { humintErrorCode, isNoAnswer, isNotMember } from './errors'
import { known, present } from './known'
import { toMarkSummaries } from './marks'
import { isEncounterType, toLatestEncounter } from './text-check'

const VERDICT_KINDS: readonly CheckVerdict['kind'][] = [
  'possible',
  'no_match',
  'retake',
  'not_checked',
  'strong',
]

export const legalStatus = known<LegalStatus>(LEGAL_STATUSES)
export const verdictKind = known<CheckVerdict['kind']>(VERDICT_KINDS)
const captureKind = known<CaptureKind>(CAPTURE_KINDS)
const skipReason = known<SkipReason>(SKIP_REASONS)
const voiceGrade = known<VoiceSampleGrade>(VOICE_SAMPLE_GRADES)

/** A nickname beside the name; none when the display name already is the nickname. */
function nicknameOf(dto: SubjectSummaryDto): string | null {
  return dto.nickname && dto.nickname !== dto.display_name ? dto.nickname : null
}

/** A subject as every workspace row shows it, with its checklist progress where the read has one. */
export function toSubjectSummary(dto: SubjectSummaryDto): SubjectSummary {
  return {
    id: dto.subject_ref,
    subjectNo: dto.short_id,
    captureTag: dto.latest_capture_tag,
    givenName: dto.display_name,
    surname: null,
    nickname: nicknameOf(dto),
    nameNative: dto.name_native,
    sex: 'unknown',
    portraitUrl: dto.portrait_url,
    legalStatus: legalStatus(dto.legal_status),
    latestEncounter: toLatestEncounter(dto.last_encounter),
    enrolledAt: dto.enrolled_at,
    ...(dto.checklist ? { checklist: dto.checklist } : {}),
  }
}

/** A row of *Subjekty*: the subject, with its open tattoo and voice leads when the read says them. */
export function toListSubject(item: SubjectListItemDto): SubjectSummary {
  const subject = toSubjectSummary(item.subject)
  const row =
    typeof item.tattoo_leads === 'number' ? { ...subject, tattooLeads: item.tattoo_leads } : subject
  return withVoiceLeads(row, item)
}

/** A person met together with the subject, as the encounter list shows them. */
export function toEncounterPerson(dto: SubjectSummaryDto): EncounterPerson {
  const { id, subjectNo, captureTag, portraitUrl, givenName, surname, sex } = toSubjectSummary(dto)
  return { id, subjectNo, captureTag, portraitUrl, givenName, surname, sex }
}

/** The encounter's point with its grid reference; null without a point. */
function toGeoPosition(place: EncounterPlaceDto | null): GeoPosition | null {
  if (!place || place.lat == null || place.lon == null) return null
  const { lat, lon } = place
  return {
    lat,
    lon,
    // Outside the UTM bands (the poles) there is no grid reference: the coordinates stand in.
    mgrs: toMgrs(lat, lon) ?? `${lat.toFixed(5)}, ${lon.toFixed(5)}`,
    accuracyM: place.accuracy_m,
    source: place.source ?? 'device',
  }
}

/** What every encounter read carries: its type, time, place and unit. */
export interface EncounterHeaderDto {
  type: string
  occurred_at: string | null
  place: EncounterPlaceDto | null
  capturing_unit: string | null
}

/** An encounter's type, time, place and unit as every encounter row shows them. */
export interface EncounterHeader {
  type: EncounterType
  at: string
  location: GeoPosition | null
  placeNote: string | null
  unit: string | null
}

/** The header of an encounter, or null when it has no time or a type the workspace does not know. */
export function toEncounterHeader(dto: EncounterHeaderDto): EncounterHeader | null {
  if (!isEncounterType(dto.type) || !dto.occurred_at) return null
  return {
    type: dto.type,
    at: dto.occurred_at,
    location: toGeoPosition(dto.place),
    placeNote: dto.place?.name ?? null,
    unit: dto.capturing_unit,
  }
}

/** The encounter a recording is held in; null for none, or for one the workspace cannot show. */
export function toRecordingEncounter(dto: RecordingEncounterDto | null): RecordingEncounter | null {
  const header = dto && toEncounterHeader(dto)
  return header ? { ref: dto.encounter_ref, ...header } : null
}

/**
 * What every read says of a recording (ADR-0057): a row of *Výsluchy*, the head of its page, and
 * a recording the subject page lists beside its subject's own.
 */
export function toRecordingListItem(dto: RecordingListItemDto): RecordingListItem {
  return {
    captureId: dto.capture_id,
    subject: toSubjectSummary(dto.subject),
    capturedAt: dto.captured_at,
    state: dto.state,
    // Read, not trusted: a stage this build does not know reads as none.
    stage: readStage(dto.stage),
    failedStage: readStage(dto.failed_stage),
    durationMs: dto.duration_ms,
    encounter: toRecordingEncounter(dto.encounter),
    participants: dto.participants.map(toSubjectSummary),
  }
}

/** One encounter of the subject, or null when it has no time or a type the workspace does not know. */
export function toEncounterSummary(
  dto: SubjectEncounterDto,
  subjectRef: string
): EncounterSummary | null {
  const header = toEncounterHeader(dto)
  if (!header) return null
  return {
    id: dto.encounter_ref,
    ...header,
    circumstances: [],
    captureTag: dto.capture_tag,
    legalStatus: legalStatus(dto.legal_status),
    subjectIds: [subjectRef, ...dto.other_subject_refs],
    checkVerdictKind: verdictKind(dto.check_verdict),
  }
}

/** The thumbnails a page read carries, by capture id (`capture_thumbnails`). */
export type CaptureThumbnails = Readonly<Record<string, string>>

const NO_THUMBNAILS: CaptureThumbnails = {}

/**
 * The picture of a stored photo, when the read carries one: an inline image, drawn as it is. A
 * recording, a voice sample and a skipped step have none, and anything that would have to be
 * fetched is left out: a tile never downloads its capture.
 */
function thumbnailOf(dto: SubjectCaptureDto, thumbnails: CaptureThumbnails): string | null {
  if (dto.kind === 'audio' || dto.kind === 'voice_sample' || dto.state === 'skipped') return null
  const thumbnail = thumbnails[dto.capture_id]
  return typeof thumbnail === 'string' && thumbnail.startsWith('data:image/') ? thumbnail : null
}

/**
 * The server's verdict on a stored voice sample (ADR-0062), read and not trusted: a grade the
 * workspace does not know is no verdict, a reason it has no words for is left out — the rest
 * stands — and the reasons come in the order they are said. Null where the server says none.
 */
export function toVoiceSampleQuality(
  dto: VoiceQualityDto | null | undefined
): VoiceSampleQuality | null {
  const grade = dto ? voiceGrade(dto.grade) : null
  if (!dto || !grade) return null
  const named = Array.isArray(dto.flags) ? dto.flags : []
  const seconds = dto.speech_seconds
  const duration = dto.duration_seconds
  const durationMs =
    typeof duration === 'number' && Number.isFinite(duration) && duration > 0
      ? Math.round(duration * 1000)
      : null
  return {
    grade,
    flags: VOICE_SAMPLE_FLAGS.filter(flag => named.includes(flag)),
    speechMs: Number.isFinite(seconds) && seconds >= 0 ? Math.round(seconds * 1000) : null,
    reference: dto.reference === true,
    ...(durationMs !== null && { durationMs }),
  }
}

/** The verdict as a capture carries it: on a voice sample that has one, and on nothing else. */
function voiceQualityField(
  kind: CaptureKind,
  dto: VoiceQualityDto | null | undefined
): Pick<CaptureSummary, 'voiceQuality'> {
  const voiceQuality = kind === 'voice_sample' ? toVoiceSampleQuality(dto) : null
  return voiceQuality ? { voiceQuality } : {}
}

/** One capture tile, or null for a kind the workspace does not know. */
export function toCaptureSummary(
  dto: SubjectCaptureDto,
  thumbnails: CaptureThumbnails = NO_THUMBNAILS
): CaptureSummary | null {
  const kind = captureKind(dto.kind)
  if (!kind) return null
  const thumbnailUrl = thumbnailOf(dto, thumbnails)
  const capture: CaptureSummary = {
    id: dto.capture_id,
    kind,
    state: dto.state,
    capturedAt: dto.captured_at,
    ...(thumbnailUrl ? { thumbnailUrl } : {}),
    ...importedFields(dto.imported, dto.taken_at),
    ...voiceQualityField(kind, dto.voice_quality),
  }
  const reason = skipReason(dto.skip_reason)
  return reason ? { ...capture, skipReason: reason } : capture
}

/** The checklist's steps (slice 03); a step of a kind the workspace does not know is left out. */
export function toChecklistSteps(
  dto: SubjectChecklistDto,
  thumbnails: CaptureThumbnails = NO_THUMBNAILS
): ChecklistStep[] {
  return dto.steps.flatMap(step => {
    const kind = captureKind(step.kind)
    if (!kind) return []
    const capture = step.capture && toCaptureSummary(step.capture, thumbnails)
    return [{ kind, required: step.required, capture }]
  })
}

/**
 * The page as the screen renders it, with the role the read was granted (a viewer's page is
 * read-only). The possible matches and the photo description are not part of this read, so their
 * cards stay hidden. A stored photo shows the thumbnail the read carries of it, in the list and
 * on the step it answers. The subject's open tattoo leads head the page when the read says them;
 * an older server says nothing, and nothing is claimed.
 */
export function toSubjectPageData(dto: SubjectPageResponse): SubjectPageData {
  const subjectRef = dto.subject.subject_ref
  const thumbnails = dto.capture_thumbnails ?? NO_THUMBNAILS
  const leads = dto.tattoo_leads
  const page: SubjectPageData = {
    ...(dto.editable && { editable: dto.editable }),
    subject: { ...toSubjectSummary(dto.subject), ...(leads && { tattooLeads: leads.count }) },
    ...(leads && { tattooLeadMarkId: leads.mark_id }),
    captures: dto.captures.map(capture => toCaptureSummary(capture, thumbnails)).filter(present),
    ...(dto.checklist && {
      checklist: toChecklistSteps(dto.checklist, thumbnails),
      checklistEncounterRef: dto.checklist.encounter_ref,
    }),
    encounters: dto.encounters
      .map(encounter => toEncounterSummary(encounter, subjectRef))
      .filter(present),
    people: Object.fromEntries(
      dto.people.map(person => [person.subject_ref, toEncounterPerson(person)])
    ),
    marks: toMarkSummaries(dto.marks, subjectRef),
    jointRecordings: (dto.joint_recordings ?? []).map(toRecordingListItem),
    memberRole: dto.member_role,
  }
  // The open voice leads head the page beside the tattoo ones, when the read says them.
  return withSubjectVoiceLeads(page, dto)
}

/** A read that did not answer with the page: not a member (the page notice), or its problem. */
export type SubjectReadFailure =
  | { kind: 'not_member' }
  | { kind: 'problem'; problem: SubjectPageProblem }

/**
 * Why the page (or the list) did not load. `online` is `navigator.onLine`: no answer while
 * offline reads "no connection". The extension gate reads as an error here; the workspace puts
 * up its wall once the extension list is refreshed.
 */
export function classifySubjectReadError(error: unknown, online: boolean): SubjectReadFailure {
  if (isNotMember(error)) return { kind: 'not_member' }
  const problem = (value: SubjectPageProblem): SubjectReadFailure => ({
    kind: 'problem',
    problem: value,
  })
  if (humintErrorCode(error) === 'audit_unavailable') return problem('audit_unavailable')
  if (error instanceof ApiError && error.status === 404) return problem('not_found')
  if (isNoAnswer(error) && !online) return problem('offline')
  return problem('error')
}

/**
 * Why a read of a list failed, in the three words every HUMINT list has: a list has no "not
 * found", so that too reads as an error.
 */
export function listReadProblem(error: unknown, online: boolean): ListReadProblem {
  const failure = classifySubjectReadError(error, online)
  if (failure.kind !== 'problem' || failure.problem === 'not_found') return 'error'
  return failure.problem
}

/** Worth one more try: no answer, or a gateway that could not reach the API. Never a refusal. */
export function isTransientReadError(error: unknown): boolean {
  if (isNoAnswer(error)) return true
  return error instanceof ApiError && [502, 504].includes(error.status)
}

/**
 * The query options of an audited read (ADR-0044): the server records the read before it answers,
 * so it is fetched once per view — no background refetch on focus or reconnect, dropped from the
 * cache as soon as nothing shows it, and retried at most once, only when no answer came back.
 */
export const AUDITED_READ = {
  staleTime: Number.POSITIVE_INFINITY,
  gcTime: 0,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  retry: (failures: number, caught: unknown) => failures < 1 && isTransientReadError(caught),
} as const
